import type { TFunction } from 'i18next'
import { File, Trash2 } from 'lucide-react'

import { X } from '@/components/Icons'
import { TableColumn } from '@/components/Table'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { FileValidationStatus } from '@/schema'

import { Alignment } from '../../components/Table/tableHelper'

import {
  CellText,
  CellTextMedium,
  ColumnHeader,
} from './components/ColumnComponents'
import { FileTypeSelect } from './components/FileTypeSelect'
import { UploadFile, UploadPhase, VersionStatus } from './types'
import { formatDocumentTypeForDisplay, formatFileSize } from './utils'

const EMPTY_VALUE_PLACEHOLDER = '--'
const ICON_SIZE = 16

export type FileUploadAction = 'delete' | 'cancel'

const getStatusLabel = (status: FileValidationStatus, t: TFunction): string => {
  const statusMap: Record<FileValidationStatus, string> = {
    // Step 1: Validation Phase
    [FileValidationStatus.VALIDATING]: t('fileUpload.status.validating'),
    [FileValidationStatus.READY_TO_UPLOAD]: t(
      'fileUpload.status.readyToUpload'
    ),
    [FileValidationStatus.FAILED]: t('fileUpload.status.failed'),
    [FileValidationStatus.ACTION_REQUIRED]: t(
      'fileUpload.status.actionRequired'
    ),
    [FileValidationStatus.CANCELLED]: t('fileUpload.status.cancelled'),

    // Step 2: Upload Phase
    [FileValidationStatus.UPLOADING]: t('fileUpload.status.uploading'),
    [FileValidationStatus.UPLOADED]: t('fileUpload.status.uploaded'),
  }

  return statusMap[status] || EMPTY_VALUE_PLACEHOLDER
}

export const getVersionStatusLabel = (
  status: VersionStatus,
  t: TFunction
): string => {
  const versionStatusMap: Record<VersionStatus, string> = {
    [VersionStatus.NEW_FILE]: t('fileUpload.versionStatus.newFile'),
    [VersionStatus.NEW_VERSION]: t('fileUpload.versionStatus.newVersion'),
  }

  return versionStatusMap[status] || EMPTY_VALUE_PLACEHOLDER
}

export const getFileUploadColumns = (
  handleActionClick: (action: FileUploadAction, file: UploadFile) => void,
  handleFileTypeChange: (fileId: string, documentType: string) => void,
  t: TFunction,
  uploadPhase: UploadPhase = UploadPhase.VALIDATION
): TableColumn<UploadFile>[] => {
  const isUploadPhase = uploadPhase === UploadPhase.UPLOAD

  const baseColumns: TableColumn<UploadFile>[] = [
    {
      id: 'fileName',
      label: <ColumnHeader>{t('fileUpload.columns.fileName')}</ColumnHeader>,
      width: 360,
      renderCell: (value, row) => {
        // Determine icon properties based on validation status
        const isCancelled =
          row.validationStatus === FileValidationStatus.CANCELLED
        const isFailed = row.validationStatus === FileValidationStatus.FAILED

        let iconColorClass = 'text-blue-500' // Default blue
        let fillValue = 'currentColor'
        let strokeWidthValue = 0

        if (isCancelled) {
          iconColorClass = 'text-[--Colors-Gray-light-300]'
          fillValue = 'none'
          strokeWidthValue = 2
        } else if (isFailed) {
          iconColorClass = 'text-red-500'
        }

        return (
          <div className="flex items-center gap-2">
            <File
              className={`flex-shrink-0 w-5 h-5 ${iconColorClass}`}
              fill={fillValue}
              strokeWidth={strokeWidthValue}
            />
            <CellTextMedium>
              {typeof value === 'string' ? (value?.toString() ?? '-') : '-'}
            </CellTextMedium>
          </div>
        )
      },
    },
    {
      id: 'versionStatus',
      label: (
        <ColumnHeader>{t('fileUpload.columns.versionStatus')}</ColumnHeader>
      ),
      width: 130,
      renderCell: (value) => (
        <CellText>
          {value
            ? getVersionStatusLabel(value as VersionStatus, t)
            : EMPTY_VALUE_PLACEHOLDER}
        </CellText>
      ),
    },
    {
      id: 'documentType',
      label: <ColumnHeader>{t('fileUpload.columns.fileType')}</ColumnHeader>,
      width: 120,
      align: Alignment.LEFT,
      cellClassName: (row) => {
        if (!isUploadPhase) {
          if (row.validationStatus === FileValidationStatus.ACTION_REQUIRED) {
            // If user has selected a type, change background to green/success color
            if (row.documentType) {
              return 'text-left'
            }
            return 'bg-[--Colors-Error-50] pl-5 text-left'
          }

          if (
            row.hasAutoDetectedType === false &&
            row.validationStatus !== FileValidationStatus.FAILED
          ) {
            return 'pl-5 text-left'
          }
        }
        return 'pl-5 text-left'
      },
      renderCell: (_, row) => {
        // Don't show dropdown for failed files (unsupported format)
        if (row.validationStatus === FileValidationStatus.FAILED) {
          return <CellText>{EMPTY_VALUE_PLACEHOLDER}</CellText>
        }

        const showDropdown =
          !isUploadPhase &&
          (row.hasAutoDetectedType === false ||
            row.validationStatus === FileValidationStatus.ACTION_REQUIRED)

        if (showDropdown) {
          return (
            <FileTypeSelect
              value={row.documentType}
              onChange={(value) => handleFileTypeChange(row.id, value)}
            />
          )
        }

        return (
          <CellText>
            {formatDocumentTypeForDisplay(row.documentType) ||
              EMPTY_VALUE_PLACEHOLDER}
          </CellText>
        )
      },
    },
    {
      id: 'fileSize',
      label: <ColumnHeader>{t('fileUpload.columns.fileSize')}</ColumnHeader>,
      width: 100,
      align: Alignment.CENTER,
      alignLabel: Alignment.CENTER,
      renderCell: (value) => (
        <span className="text-sm">
          {value !== undefined && value !== null
            ? formatFileSize(Number(value))
            : EMPTY_VALUE_PLACEHOLDER}
        </span>
      ),
    },
    {
      id: 'validationStatus',
      label: (
        <ColumnHeader>
          {isUploadPhase
            ? t('fileUpload.columns.uploadingStatus')
            : t('fileUpload.columns.validationStatus')}
        </ColumnHeader>
      ),
      width: 205,
      alignLabel: Alignment.LEFT,
      align: Alignment.LEFT,
      renderCell: (value, row) => {
        const status = value as FileValidationStatus
        const statusLabel = getStatusLabel(status, t)
        const showErrorMessage =
          status === FileValidationStatus.FAILED && row.errorMessage

        if (showErrorMessage) {
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-sm text-tertiary-600">{statusLabel}</span>
              <span className="text-xs text-red-600">{row.errorMessage}</span>
            </div>
          )
        }

        const statusStyles: Record<FileValidationStatus, string> = {
          // Step 1: Validation Phase
          [FileValidationStatus.VALIDATING]: 'text-tertiary-600 bg-transparent',
          [FileValidationStatus.READY_TO_UPLOAD]:
            'text-tertiary-600 bg-transparent',
          [FileValidationStatus.FAILED]: 'text-red-600 bg-transparent',
          [FileValidationStatus.ACTION_REQUIRED]:
            'text-error-primary-600 bg-transparent',
          [FileValidationStatus.CANCELLED]: 'text-red-600 bg-transparent',

          // Step 2: Upload Phase
          [FileValidationStatus.UPLOADING]: 'text-tertiary-600 bg-transparent',
          [FileValidationStatus.UPLOADED]: 'text-tertiary-600 bg-transparent',
        }

        return (
          <span className={`text-sm ${statusStyles[status] || ''}`}>
            {statusLabel}
          </span>
        )
      },
    },
  ]

  if (!isUploadPhase) {
    baseColumns.push({
      id: 'actions',
      width: 70,
      renderCell: (_, row) => {
        if (row.validationStatus === FileValidationStatus.VALIDATING) {
          return (
            <div className="flex items-center justify-center gap-0.5">
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="text"
                    className="p-1.5 text-foreground-fg-quaternary-400 hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActionClick('cancel', row)
                    }}
                  >
                    <X size={ICON_SIZE} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <span>{t('fileUpload.actions.cancel')}</span>
                </TooltipContent>
              </Tooltip>
            </div>
          )
        }

        if (row.validationStatus === FileValidationStatus.CANCELLED) {
          return null
        }

        return (
          <div className="flex items-center justify-center gap-0.5">
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="text"
                  className="p-1.5 text-foreground-fg-quaternary-400 hover:text-red-600"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleActionClick('delete', row)
                  }}
                >
                  <Trash2 size={ICON_SIZE} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <span>{t('fileUpload.actions.delete')}</span>
              </TooltipContent>
            </Tooltip>
          </div>
        )
      },
    })
  }

  return baseColumns
}
